import { useEffect, useState } from "react";
import { MemberPortalApi, type PortalAccount, getMemberToken } from "@/api/perkpoint/memberPortal";

const WALLET_EVENT = "member-wallet";

export type MemberWallet = {
  points: number;
  pendingPoints: number;
  qualifyingSpend: number;
  savedInr: number;
  tierName: string;
  currency: string;
  pointValue: number;
  programName: string;
  enrollmentDate: string;
  fullName: string;
  email: string;
  mobile: string;
  memberId: string;
  initials: string;
  city: string;
  loaded: boolean;
  benefits: string[];
  nextTier: string | null;
  remaining: number;
  progressPct: number;
};

const EMPTY: MemberWallet = {
  points: 0,
  pendingPoints: 0,
  qualifyingSpend: 0,
  savedInr: 0,
  tierName: "Member",
  currency: "AED",
  pointValue: 1,
  programName: "Perk Point",
  enrollmentDate: "",
  fullName: "",
  email: "",
  mobile: "",
  memberId: "",
  initials: "",
  city: "",
  loaded: false,
  benefits: [],
  nextTier: null,
  remaining: 0,
  progressPct: 0,
};

let cache: MemberWallet | null = null;
let inflight: Promise<MemberWallet> | null = null;

function formatJoined(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function accountToWallet(account: PortalAccount): MemberWallet {
  const next = account.progress?.[0];
  const target = next?.next_entry_major;
  const value = next?.value_major ?? account.qualifying_spend;
  const progressPct = target && target > 0 ? Math.min(100, Math.round((value / target) * 100)) : next ? 100 : 0;
  return {
    points: account.points?.available ?? 0,
    pendingPoints: account.points?.pending ?? 0,
    qualifyingSpend: account.qualifying_spend ?? 0,
    savedInr: account.saved ?? 0,
    tierName: account.tier?.name || "Member",
    currency: account.program?.currency || "AED",
    pointValue: Number(account.program?.point_value) || 1,
    programName: account.program?.name || "Perk Point",
    enrollmentDate: formatJoined(account.enrolled_at),
    fullName: account.full_name,
    email: account.email || "",
    mobile: account.phone || "",
    memberId: account.external_member_id,
    initials: account.initials,
    city: account.city || "",
    loaded: true,
    benefits: (account.benefits || []).map((benefit) => benefit.name),
    nextTier: next?.next_tier_name || next?.next_tier_key || null,
    remaining: next?.remaining_major ?? 0,
    progressPct,
  };
}

export function clearWallet() {
  cache = null;
  inflight = null;
  window.dispatchEvent(new Event(WALLET_EVENT));
}

export async function refreshWallet(): Promise<MemberWallet> {
  if (!getMemberToken()) return EMPTY;
  if (!inflight) {
    inflight = MemberPortalApi.account()
      .then((account) => {
        cache = accountToWallet(account);
        window.dispatchEvent(new Event(WALLET_EVENT));
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function useWallet() {
  const [wallet, setWallet] = useState<MemberWallet>(cache ?? EMPTY);

  useEffect(() => {
    const sync = () => setWallet(cache ?? EMPTY);
    window.addEventListener(WALLET_EVENT, sync);
    if (cache) setWallet(cache);
    else void refreshWallet().then(setWallet).catch(() => setWallet(EMPTY));
    return () => window.removeEventListener(WALLET_EVENT, sync);
  }, []);

  return wallet;
}
